/**
 * Compact SVG sparkline. Renders a line with dots for each data point.
 *
 * Props:
 * - points: [{score:number, at:string}]
 * - width, height: dimensions (default 140x36)
 * - stroke: line color (default amber-500)
 * - fill: area fill color (default amber-100 with opacity)
 */
export function Sparkline({ points, width = 140, height = 36, stroke = "#F59E0B", fill = "rgba(245,158,11,0.15)" }) {
  if (!points || points.length === 0) {
    return (
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400" data-testid="sparkline-empty">
        No history yet
      </div>
    );
  }

  const pad = 3;
  const w = width;
  const h = height;
  const scores = points.map((p) => Math.max(0, Math.min(100, p.score)));
  const min = 0;
  const max = 100;
  const range = max - min || 1;

  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const coords = scores.map((s, i) => {
    const x = pad + (points.length > 1 ? step * i : (w - pad * 2) / 2);
    const y = h - pad - ((s - min) / range) * (h - pad * 2);
    return [x, y];
  });

  const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${coords[coords.length - 1][0].toFixed(1)},${h - pad} L${coords[0][0].toFixed(1)},${h - pad} Z`;

  const first = scores[0];
  const last = scores[scores.length - 1];
  const delta = last - first;
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const dirColor = dir === "up" ? "text-emerald-600" : dir === "down" ? "text-red-600" : "text-stone-500";
  const dirSymbol = dir === "up" ? "▲" : dir === "down" ? "▼" : "→";

  return (
    <div className="inline-flex items-center gap-2" data-testid="sparkline">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
        {points.length > 1 && <path d={area} fill={fill} />}
        {points.length > 1 && <path d={line} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />}
        {coords.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === coords.length - 1 ? 3 : 1.75} fill={i === coords.length - 1 ? stroke : "#fff"} stroke={stroke} strokeWidth="1" />
        ))}
      </svg>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${dirColor}`}>
        {dirSymbol} {delta > 0 ? `+${delta}` : delta}
      </div>
    </div>
  );
}
